
import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Utensils, Music, Users, Clock } from "lucide-react";
import FeatureCard from "../components/FeatureCard";

const About: React.FC = () => {
  const features = [
    {
      icon: <Utensils size={36} />,
      title: "Premium Food & Drinks",
      description: "Enjoy our diverse menu featuring appetizers, main courses, and signature beverages crafted to complement your hookah experience."
    },
    {
      icon: <Music size={36} />,
      title: "Regular Events",
      description: "From DJ nights to live performances, our regular events ensure there's always something exciting happening at Seven8Nine."
    },
    {
      icon: <Users size={36} />,
      title: "Private Parties",
      description: "Book our space for private celebrations, corporate events, or special occasions with customized packages."
    },
    {
      icon: <Clock size={36} />,
      title: "Extended Hours",
      description: "Open late on weekends, we're the perfect destination for your evening entertainment and relaxation."
    }
  ];

  return (
    <div className="min-h-screen bg-lounge-dark text-lounge-light">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-20 pb-16 md:pt-24 md:pb-20 bg-lounge-charcoal">
        <div className="container-custom">
          <h1 className="section-title text-center">About Us</h1>
          <p className="section-subtitle text-center max-w-3xl mx-auto">
            Discover the story behind Winnipeg's premier hookah lounge experience.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <section className="section-padding bg-lounge-dark">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold mb-6 text-lounge-gold">Our Story</h2>
              <p className="text-lounge-light/80 mb-4">
                Seven8Nine Hookah Lounge was established with a vision to create an unparalleled hookah experience in Winnipeg. Founded by a group of friends passionate about hookah culture and hospitality, our lounge has quickly become a staple in the city's nightlife scene.
              </p>
              <p className="text-lounge-light/80 mb-4">
                We recognized the need for a premium hookah establishment that doesn't compromise on quality, atmosphere, or service. Our founders traveled extensively, studying hookah traditions from the Middle East and modern lounge concepts from around the world, bringing the best elements together in one exceptional space.
              </p>
              <p className="text-lounge-light/80">
                Since opening our doors, we've been committed to constant innovation and improvement. From expanding our flavor selection to enhancing our food and drink menu, every change is made with our customers' satisfaction in mind.
              </p>
            </div>
            <div className="relative h-[400px] rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1721322800607-8c38375eef04?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80" 
                alt="Seven8Nine Lounge Interior" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="section-padding bg-gradient-to-b from-lounge-dark to-lounge-charcoal">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-6 text-lounge-gold">Our Vision</h2>
            <p className="text-xl text-lounge-light/80 mb-6">
              At Seven8Nine, we strive to be more than just a hookah lounge. Our vision is to create a cultural hub where people can come together to socialize, relax, and enjoy authentic experiences in a luxurious setting.
            </p>
            <div className="w-24 h-1 bg-lounge-gold mx-auto my-8"></div>
            <p className="italic text-lounge-light/70">
              "We believe in creating moments and memories, not just serving hookah. Every detail of our establishment is designed to transport you to a world of luxury and relaxation."
            </p>
            <p className="mt-4 text-lounge-gold font-display">— Seven8Nine Management Team</p>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-lounge-charcoal">
        <div className="container-custom">
          <h2 className="section-title text-center">What We Offer</h2>
          <p className="section-subtitle text-center max-w-3xl mx-auto">
            Explore the range of experiences available at Seven8Nine Hookah Lounge.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-lounge-dark">
        <div className="container-custom">
          <h2 className="section-title text-center">What Our Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
              <div className="flex items-center mb-4">
                <div className="flex text-lounge-gold">
                  {'★★★★★'.split('').map((star, i) => <span key={i}>{star}</span>)}
                </div>
              </div>
              <p className="text-lounge-light/80 italic mb-4">
                "The best hookah experience in Winnipeg! The ambiance is perfect, and the staff is knowledgeable and attentive. I'm a regular customer now."
              </p>
              <p className="font-semibold text-lounge-gold">— Michael T.</p>
            </div>
            <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
              <div className="flex items-center mb-4">
                <div className="flex text-lounge-gold">
                  {'★★★★★'.split('').map((star, i) => <span key={i}>{star}</span>)}
                </div>
              </div>
              <p className="text-lounge-light/80 italic mb-4">
                "Seven8Nine is my go-to place for a night out. The food is delicious, the hookah flavors are unique, and the overall atmosphere is unmatched."
              </p>
              <p className="font-semibold text-lounge-gold">— Sarah K.</p>
            </div>
            <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
              <div className="flex items-center mb-4">
                <div className="flex text-lounge-gold">
                  {'★★★★★'.split('').map((star, i) => <span key={i}>{star}</span>)}
                </div>
              </div>
              <p className="text-lounge-light/80 italic mb-4">
                "I hosted my birthday party here and it was an absolute hit! The staff went above and beyond to make it special. Highly recommend for private events."
              </p>
              <p className="font-semibold text-lounge-gold">— David L.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
